import type { Character } from '../schemas/character';
import type { ClassDefinition } from '../schemas/classDefinition';
import { findMoveByIdAnyScope, findMoveByIdGlobal } from '../data/moves';
import { hasMoveSetup } from '../schemas/move';
import { getAvailableCommonMoves } from './commonMovesAvailability';
import {
  getAvailableAdvancedMoves,
  isLevelUpAvailable,
  LEVEL_FORMULA_CAP,
  STAT_CAP,
  type LevelUpInput,
} from './levelUp';
import { validateMulticlassPick } from './multiclass';
import { getGrantedPool } from './moveGrants';

/**
 * **D10 — FI-11**: validatori dell'input di `applyLevelUp`.
 *
 * Estratti da `services/levelUp.ts` per rispettare `max-lines: 400` quando
 * D10 ha aggiunto i nuovi check `pastTen` + post-10° su `statIncreased`.
 *
 * API pubblica: {@link validateLevelUpInput} (entry-point composito).
 * Le sub-funzioni `validate*` restano locali al modulo.
 */
export function validateLevelUpInput(
  character: Character,
  input: LevelUpInput,
  classDef: ClassDefinition,
): void {
  if (!isLevelUpAvailable(character)) {
    if (character.pastTen === 'addio') {
      throw new Error(
        `PG ritirato (L'Addio scelto al L${character.level}). Nessun nuovo level-up consentito.`,
      );
    }
    throw new Error(`PG non eleggibile per level-up al L${character.level}.`);
  }
  validatePastTen(character, input);
  validateStatIncrement(character, input);
  validateMoveAvailability(character, input, classDef);
  validateSecondaryArchetype(character, input, classDef);
  validateGrantedChoice(character, input, classDef);
  validateMulticlassPickInput(character, input, classDef);
}

/**
 * **D10 — FI-11**: validazione del campo `pastTen` dell'input.
 *  - Se PG sta passando da L10 a L11 (`targetLevel === 11`): `pastTen`
 *    DEVE essere fornito (Addio / Apprendista / Instancabile).
 *  - Se PG è già post-10° (`character.level >= 11`): `pastTen` NON deve
 *    essere fornito (decisione già presa, persiste su `Character.pastTen`).
 *  - Se PG è L1-L9 (`targetLevel <= 10`): `pastTen` NON deve essere fornito.
 */
function validatePastTen(character: Character, input: LevelUpInput): void {
  const targetLevel = character.level + 1;
  const isCrossingTen = targetLevel === LEVEL_FORMULA_CAP + 1; // L10 → L11
  if (isCrossingTen) {
    if (input.pastTen === undefined) {
      throw new Error(
        `Il passaggio L10 → L11 richiede una scelta cruciale (pastTen): 'addio', 'apprendista' o 'instancabile' (manuale V1 r. 1824-1834).`,
      );
    }
    return;
  }
  if (input.pastTen !== undefined) {
    throw new Error(
      `Il campo "pastTen" può essere fornito SOLO al passaggio L10 → L11. Livello target attuale: L${targetLevel}.`,
    );
  }
}

function validateMulticlassPickInput(
  character: Character,
  input: LevelUpInput,
  classDef: ClassDefinition,
): void {
  const moveDef = findMoveByIdAnyScope(classDef.id, input.moveAdded.id);
  if (!moveDef) return;
  const hasMulticlass = moveDef.multiclass !== undefined;
  if (!hasMulticlass) {
    if (input.multiclassPick !== undefined) {
      throw new Error(
        `La mossa "${moveDef.id}" non è una mossa multiclasse, ma "multiclassPick" è stato fornito.`,
      );
    }
    return;
  }
  if (input.multiclassPick === undefined) {
    throw new Error(
      `La mossa "${moveDef.id}" è multiclasse (FI-17), ma "multiclassPick" non è stato fornito.`,
    );
  }
  const err = validateMulticlassPick(
    character,
    moveDef,
    {
      sourceClassId: input.multiclassPick.sourceClassId,
      selectedMoveId: input.multiclassPick.selectedMoveId,
    },
    classDef,
  );
  if (err !== null) throw new Error(err);
  validateMulticlassGrantedOfTarget(input);
}

/**
 * **FI-84 enhanced (2026-05-24)** — Validazione cascade di mosse concesse
 * via `grantsMoveChoice` sulla mossa-target multiclass (e ricorsivamente).
 *
 * Modello: `multiclassPick.grantedChain` è una lista di step. Step `[0]`
 * = granted dalla target. Step `[1]` = granted-of-step-[0]. Etc.
 *
 * Validazioni per ogni step:
 *  - L'anchor di quel livello deve avere `grantsMoveChoice`.
 *  - `selectedMoveId` deve essere nel pool della grant.
 *  - Se la mossa scelta ha `hasMoveSetup`, `selectedSetup` deve essere
 *    fornito; altrimenti deve essere assente.
 *
 * Per `from-choice-mapping`: il primo step granted è auto-derivato dal
 * mapping della `selectedChoices[0]` del target setup → il chain
 * esplicito può ometterlo (validation considera il mapping pre-pended).
 */
function validateMulticlassGrantedOfTarget(input: LevelUpInput): void {
  const pick = input.multiclassPick;
  if (pick === undefined) return;
  const targetMove = findMoveByIdGlobal(pick.selectedMoveId);
  const grant = targetMove?.grantsMoveChoice;
  const chain = pick.grantedChain ?? [];
  if (grant === undefined) {
    if (chain.length > 0) {
      throw new Error(
        `La mossa multiclasse "${pick.selectedMoveId}" non concede alcuna mossa cross-class, ma "grantedChain" non è vuoto.`,
      );
    }
    return;
  }
  // Risolvi cascade attesa partendo dal target (replica logica di
  // `buildMulticlassGrantedCascade`). Validation walks attraverso i
  // livelli per verificare ogni step. `targetMove` è non-undefined qui:
  // il caso `grant === undefined` (che include `targetMove === undefined`)
  // è già gestito dalla guard sopra.
  if (targetMove === undefined) return;
  validateGrantedCascadeChain(pick, targetMove, chain);
}

function validateGrantedCascadeChain(
  pick: NonNullable<LevelUpInput['multiclassPick']>,
  targetMove: NonNullable<ReturnType<typeof findMoveByIdGlobal>>,
  chain: NonNullable<LevelUpInput['multiclassPick']>['grantedChain'],
): void {
  const grant = targetMove.grantsMoveChoice;
  // **FI-84 enhanced**: per `from-choice-mapping`, il primo step è
  // auto-derivato dal mapping. Coerentemente con `buildMulticlassGrantedCascade`,
  // pre-pendiamo lo step mappato alla chain prima del walking.
  const normalizedChain = normalizeChainForCascade(pick, grant, chain);
  if (normalizedChain === undefined || normalizedChain.length === 0) {
    if (grant?.kind === 'from-list') {
      throw new Error(
        `La mossa multiclasse "${pick.selectedMoveId}" concede una scelta from-list ma "grantedChain[0]" non è stato fornito.`,
      );
    }
    return;
  }
  // Walk: anchor iniziale = target (per la sua grant). I successivi sono
  // le mosse dei livelli precedenti.
  let currentAnchor: NonNullable<ReturnType<typeof findMoveByIdGlobal>> = targetMove;
  for (const step of normalizedChain) {
    const stepGrant = currentAnchor.grantsMoveChoice;
    if (stepGrant === undefined) {
      throw new Error(
        `Cascade granted: "${currentAnchor.id}" non ha grantsMoveChoice, ma uno step "${step.selectedMoveId}" è stato fornito.`,
      );
    }
    validateCascadeStep(stepGrant, step.selectedMoveId, currentAnchor.id, pick);
    const grantedMove = findMoveByIdGlobal(step.selectedMoveId);
    if (grantedMove === undefined) {
      throw new Error(
        `Cascade granted: la mossa "${step.selectedMoveId}" non è nel catalogo globale.`,
      );
    }
    if (hasMoveSetup(grantedMove)) {
      if (step.selectedSetup === undefined) {
        throw new Error(
          `Cascade granted: la mossa "${step.selectedMoveId}" richiede setup interattivo, ma "selectedSetup" non è stato fornito.`,
        );
      }
    } else if (step.selectedSetup !== undefined) {
      throw new Error(
        `Cascade granted: la mossa "${step.selectedMoveId}" non ha setup interattivo, ma "selectedSetup" è stato fornito.`,
      );
    }
    currentAnchor = grantedMove;
  }
}

function normalizeChainForCascade(
  pick: NonNullable<LevelUpInput['multiclassPick']>,
  grant: NonNullable<ReturnType<typeof findMoveByIdGlobal>>['grantsMoveChoice'],
  chain: NonNullable<LevelUpInput['multiclassPick']>['grantedChain'],
): NonNullable<LevelUpInput['multiclassPick']>['grantedChain'] {
  if (grant?.kind !== 'from-choice-mapping') return chain;
  const selectedChoice = pick.selectedMoveSetup?.selectedChoices?.[0];
  if (selectedChoice === undefined) return chain;
  const mappedId = grant.mapping[selectedChoice];
  if (mappedId === undefined) return chain;
  const existing = chain ?? [];
  if (existing[0]?.selectedMoveId === mappedId) return existing;
  return [{ selectedMoveId: mappedId }, ...existing];
}

function validateCascadeStep(
  grant: NonNullable<ReturnType<typeof findMoveByIdGlobal>>['grantsMoveChoice'],
  selectedMoveId: string,
  anchorId: string,
  pick: NonNullable<LevelUpInput['multiclassPick']>,
): void {
  if (grant?.kind === 'from-list') {
    if (!grant.moveOptions.includes(selectedMoveId)) {
      throw new Error(
        `Cascade granted: "${selectedMoveId}" non è nel pool from-list di "${anchorId}".`,
      );
    }
    return;
  }
  if (grant?.kind === 'from-choice-mapping') {
    const mappedIds = Object.values(grant.mapping);
    if (!mappedIds.includes(selectedMoveId)) {
      throw new Error(
        `Cascade granted: "${selectedMoveId}" non è nei target del mapping di "${anchorId}".`,
      );
    }
    // Per from-choice-mapping serve anche che la selectedChoice del
    // sorgente (anchor) sia compatibile col mapping. Skip qui (logica
    // semantica vive nel resolve della cascata).
    void pick;
    return;
  }
  if (grant?.kind === 'from-group') {
    // Pool from-group richiede `classDef.startingChoiceGroups` — skip
    // validation puntuale qui, la cascata service runtime già throws.
    return;
  }
  throw new Error(
    `Cascade granted: kind "${grant?.kind}" della grant di "${anchorId}" non è gestito dalla cascade.`,
  );
}

function validateGrantedChoice(
  character: Character,
  input: LevelUpInput,
  classDef: ClassDefinition,
): void {
  const moveDef = findMoveByIdAnyScope(classDef.id, input.moveAdded.id);
  if (!moveDef) return;
  const grant = moveDef.grantsMoveChoice;
  if (grant === undefined) {
    if (input.grantedChoice !== undefined) {
      throw new Error(
        `La mossa "${moveDef.id}" non concede alcuna sotto-scelta, ma "grantedChoice" è stato fornito.`,
      );
    }
    return;
  }
  if (input.grantedChoice === undefined) {
    throw new Error(
      `La mossa "${moveDef.id}" concede una sotto-scelta (FI-31), ma "grantedChoice" non è stato fornito.`,
    );
  }
  const pool = getGrantedPool(character, grant, classDef);
  if (!pool.includes(input.grantedChoice.selectedOptionId)) {
    throw new Error(
      `L'opzione "${input.grantedChoice.selectedOptionId}" non è nel pool residuo della grant di "${moveDef.id}".`,
    );
  }
}

function validateStatIncrement(character: Character, input: LevelUpInput): void {
  const statId = input.statIncreased;
  // **D10 — FI-11**: post-10° Apprendista/Instancabile NON aumenta più le
  // Caratteristiche (manuale r. 1834). `statIncreased` deve essere undefined.
  const isPostTenContinuing =
    character.level >= LEVEL_FORMULA_CAP &&
    (input.pastTen === 'apprendista' ||
      input.pastTen === 'instancabile' ||
      character.pastTen === 'apprendista' ||
      character.pastTen === 'instancabile');
  if (statId === undefined) {
    if (!isPostTenContinuing) {
      throw new Error(
        `Il level-up L${character.level} → L${character.level + 1} richiede una Caratteristica da aumentare (statIncreased).`,
      );
    }
    return;
  }
  if (isPostTenContinuing) {
    throw new Error(
      `Post-10° Apprendista/Instancabile: le Caratteristiche non aumentano più (manuale V1 r. 1834). Rimuovere "statIncreased".`,
    );
  }
  if (character.stats[statId] >= STAT_CAP) {
    throw new Error(
      `Stat "${statId}" già al cap ${STAT_CAP}. Non può essere aumentata ulteriormente.`,
    );
  }
}

function validateMoveAvailability(
  character: Character,
  input: LevelUpInput,
  classDef: ClassDefinition,
): void {
  const targetLevel = character.level + 1;
  const advanced = getAvailableAdvancedMoves(character, targetLevel, classDef);
  const common = getAvailableCommonMoves(character, targetLevel);
  const isAvailable =
    advanced.some((m) => m.id === input.moveAdded.id) ||
    common.some((m) => m.id === input.moveAdded.id);
  if (!isAvailable) {
    throw new Error(
      `Mossa "${input.moveAdded.id}" non disponibile al level-up verso L${targetLevel} (prereq non soddisfatti, già posseduta, tipo non compatibile, o non è una mossa comune ammessa).`,
    );
  }
}

function validateSecondaryArchetype(
  character: Character,
  input: LevelUpInput,
  classDef: ClassDefinition,
): void {
  if (input.moveAdded.id === 'common.infanzia-complessa') {
    if (input.secondaryArchetypeId === undefined) {
      throw new Error('Infanzia Complessa richiede `secondaryArchetypeId`.');
    }
    if (input.secondaryArchetypeId === character.archetypeId) {
      throw new Error(
        `Il secondo archetipo (${input.secondaryArchetypeId}) deve essere diverso dal primario.`,
      );
    }
    const arch = classDef.archetypes.find((a) => a.id === input.secondaryArchetypeId);
    if (arch === undefined) {
      throw new Error(
        `Archetipo "${input.secondaryArchetypeId}" non esiste per la classe "${classDef.id}".`,
      );
    }
    if (character.secondaryArchetypeId !== undefined) {
      throw new Error('Il PG ha già un secondo archetipo (Infanzia Complessa già presa).');
    }
    // FI-80 (2026-05-24): se la mossa-archetipo del 2° archetipo ha
    // `hasMoveSetup`, l'input deve fornire `secondaryArchetypeSetup`.
    // Viceversa, una mossa-archetipo description-only deve avere setup
    // assente (evita drift di MoveRef sporco).
    const archMove = findMoveByIdGlobal(arch.grantedMoveId);
    if (archMove !== undefined && hasMoveSetup(archMove)) {
      if (input.secondaryArchetypeSetup === undefined) {
        throw new Error(
          `La mossa-archetipo "${arch.grantedMoveId}" richiede setup interattivo, ma "secondaryArchetypeSetup" non è stato fornito.`,
        );
      }
    } else if (input.secondaryArchetypeSetup !== undefined) {
      throw new Error(
        `La mossa-archetipo "${arch.grantedMoveId}" non ha setup interattivo: "secondaryArchetypeSetup" non deve essere fornito.`,
      );
    }
  } else {
    if (input.secondaryArchetypeId !== undefined) {
      throw new Error(
        '`secondaryArchetypeId` può essere specificato SOLO con la mossa *Infanzia Complessa*.',
      );
    }
    if (input.secondaryArchetypeSetup !== undefined) {
      throw new Error(
        '`secondaryArchetypeSetup` può essere specificato SOLO con la mossa *Infanzia Complessa*.',
      );
    }
  }
}
